import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

export interface Comparison {
  year: number;
  samePeriod: boolean;
}

export interface AnalyticsMeta {
  year: number;
  comparison: Comparison | null;
}

export interface KpiValue {
  value: number;
  previous: number | null;
}

export interface MonthlySeries {
  current: number[];
  previous: number[] | null;
}

export interface YearlyCount {
  year: number;
  count: number;
}

export interface ServiceTypeCount {
  type: string;
  count: number;
}

export interface OverviewResponse {
  meta: AnalyticsMeta;
  kpis: {
    reports: KpiValue;
    machinesServed: KpiValue;
    sparePartsUsed: KpiValue;
    travelDays: KpiValue;
    workHours: KpiValue;
    travelHours: KpiValue;
    travelKm: KpiValue;
  };
  reportsByMonth: MonthlySeries;
  reportsByYear: YearlyCount[];
  machinesByServiceType: ServiceTypeCount[];
}

export interface TechnicianRow {
  userId: string;
  name: string;
  reports: number;
  asSupport: number;
  travelDays: number;
  workHours: number;
  travelHours: number;
  machinesServed: number;
  partsSent: number;
  partsUsed: number;
  usedPct: number;
}

export interface TechniciansResponse {
  meta: AnalyticsMeta;
  technicians: TechnicianRow[];
}

export interface CustomerRow {
  customerId: string;
  name: string;
  reports: number;
  workHours: number;
  partsUsed: number;
  lastReportDate: string;
}

export interface CustomersResponse {
  meta: AnalyticsMeta;
  top: CustomerRow[];
}

export interface MachineRow {
  machineId: string;
  serialNumber: string;
  customerName: string | null;
  reports: number;
  workHours: number;
  partsUsed: number;
}

export interface MachinesResponse {
  meta: AnalyticsMeta;
  top: MachineRow[];
}

export interface SparePartRankingRow {
  productCode: string;
  descriptionIt: string | null;
  descriptionEn: string | null;
  used: number;
  sent: number;
  usagePct: number;
}

export interface SparePartsKpis {
  sent: number;
  used: number;
  usageRatePct: number;
  distinctParts: number;
}

export interface SparePartsResponse {
  meta: AnalyticsMeta;
  kpis: SparePartsKpis;
  topUsed: SparePartRankingRow[];
  topSent: SparePartRankingRow[];
  topUsagePct: SparePartRankingRow[];
  bottomUsagePct: SparePartRankingRow[];
  pctThreshold: number;
}

export interface LeadTimeDto {
  sampleSize: number;
  avgHours: number | null;
}

export interface StateTimeDto {
  status: string;
  occurrences: number;
  avgHours: number;
}

export interface PathVariantDto {
  path: string;
  reportCount: number;
}

export interface RejectedReasonDto {
  reason: string | null;
  occurrences: number;
}

export interface WorkflowOverviewResponse {
  year: number;
  leadTimeNewToClosed: LeadTimeDto;
  timeInState: StateTimeDto[];
  pathVariants: PathVariantDto[];
  rejectedByReason: RejectedReasonDto[];
}

export interface ReportTimelineEventDto {
  fromStatus: string | null;
  toStatus: string | null;
  occurredAt: string;
  hoursSincePrevious: number | null;
  origin: string;
}

export interface ReportTimelineResponse {
  reportId: string;
  reportNumber: string;
  events: ReportTimelineEventDto[];
  totalLeadTimeHours: number | null;
}

export type FailureDimension = 'COMPONENT' | 'SYMPTOM' | 'CAUSE' | 'ACTION';

/** `family` null = machine not identified; `code` null = fact not attributed on this axis. */
export interface FailureMatrixCell {
  family: string | null;
  code: string | null;
  labelIt: string | null;
  labelEn: string | null;
  occurrences: number;
}

/** `servedMachines` is null for the not-identified bucket, where a per-machine rate has no meaning. */
export interface FailureFamilyTotal {
  family: string | null;
  facts: number;
  failureRows: number;
  servedMachines: number | null;
}

export interface FailureServiceTypeCount {
  serviceType: string;
  failureRows: number;
}

export interface FailureMatrixTotals {
  facts: number;
  failureRows: number;
  servedMachines: number;
  unattributedFacts: number;
  unattributedWithText: number;
  unattributedWithoutText: number;
  unidentifiedFacts: number;
  unidentifiedServedMachines: number;
}

export interface FailureMatrixResponse {
  dimension: FailureDimension;
  cells: FailureMatrixCell[];
  families: FailureFamilyTotal[];
  totals: FailureMatrixTotals;
  byServiceType: FailureServiceTypeCount[];
  minMachinesForRate: number;
}

export interface FailureYearCount {
  year: number;
  facts: number;
  failureRows: number;
}

export interface FamilySparePart {
  productCode: string;
  descriptionIt: string | null;
  descriptionEn: string | null;
  used: number;
}

export interface FailureFamilyDetailResponse {
  family: string;
  servedMachines: number;
  facts: number;
  failureRows: number;
  byYear: FailureYearCount[];
  topSpareParts: FamilySparePart[];
}

export interface FailureOutcomeCount {
  outcome: string;
  rows: number;
}

export interface FailureCoverageResponse {
  closedRows: number;
  byOutcome: FailureOutcomeCount[];
}

async function fetchAnalytics<T>(path: string): Promise<T> {
  const authHeader = authService.getAuthorizationHeader();
  const headers: Record<string, string> = {};
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }

  const response = await fetch(`${API_BASE_URL}/analytics/service${path}`, { headers });

  if (!response.ok) {
    let message = `Errore ${response.status}`;
    try {
      const data = await response.json();
      if (data?.message) message = data.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }

  return response.json();
}

export const analyticsService = {
  getOverview: (year: number): Promise<OverviewResponse> =>
    fetchAnalytics<OverviewResponse>(`/overview?year=${year}`),
  getTechnicians: (year: number): Promise<TechniciansResponse> =>
    fetchAnalytics<TechniciansResponse>(`/technicians?year=${year}`),
  getCustomers: (year: number): Promise<CustomersResponse> =>
    fetchAnalytics<CustomersResponse>(`/customers?year=${year}`),
  getMachines: (year: number): Promise<MachinesResponse> =>
    fetchAnalytics<MachinesResponse>(`/machines?year=${year}`),
  getSpareParts: (year: number): Promise<SparePartsResponse> =>
    fetchAnalytics<SparePartsResponse>(`/spare-parts?year=${year}`),
  getWorkflowOverview: (year: number): Promise<WorkflowOverviewResponse> =>
    fetchAnalytics<WorkflowOverviewResponse>(`/workflow/overview?year=${year}`),
  getReportTimeline: (reportId: string): Promise<ReportTimelineResponse> =>
    fetchAnalytics<ReportTimelineResponse>(`/workflow/report/${reportId}`),
  getFailureMatrix: (dimension: FailureDimension): Promise<FailureMatrixResponse> =>
    fetchAnalytics<FailureMatrixResponse>(`/failures/matrix?dimension=${dimension}`),
  getFailureFamilyDetail: (family: string): Promise<FailureFamilyDetailResponse> =>
    fetchAnalytics<FailureFamilyDetailResponse>(`/failures/family?name=${encodeURIComponent(family)}`),
  getFailureCoverage: (): Promise<FailureCoverageResponse> =>
    fetchAnalytics<FailureCoverageResponse>('/failures/coverage'),
};
